import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Spin, Tooltip } from 'antd';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrencyByCurrencyId,
} from '../../utils/DataAccessUtils';

interface PartEquipmentPricePickerProps {
	entityId: any;
	entityName: 'partId' | 'equipmentTypeId';
	vendorId: any;
	fetchMethod: any;
	fallbackCurrencyId: any;
	value: any;
	onChange: (unitPrice: any) => void;
	onCatalogLoad?: (catalog: any, catalogCurrencyId: any) => void;
	disabled?: boolean;
	// When true, the picker disables itself (and skips any default selection) if the
	// loaded catalog's currency differs from fallbackCurrencyId — its prices would be
	// in a currency the consumer can't use. Opt-in so other consumers are unaffected.
	disableOnCurrencyMismatch?: boolean;
	disabledTooltip?: React.ReactNode;
	defaultToListPrice?: boolean;
	entityCost?: any;
}

const PartEquipmentPricePicker: FC<PartEquipmentPricePickerProps> = ({
	entityId,
	entityName,
	vendorId,
	fetchMethod,
	fallbackCurrencyId,
	value,
	onChange,
	onCatalogLoad,
	disabled,
	disableOnCurrencyMismatch,
	disabledTooltip,
	defaultToListPrice,
	entityCost,
}) => {
	const [catalog, setCatalog] = useState(null);
	// Treat a pending vendor fetch as "loading" from the very first render so the
	// default-selection logic below never reads an empty catalog and wrongly
	// falls back to cost before the real list/last price has had a chance to
	// arrive. Without a vendor there is no fetch, so we start un-loaded.
	const [catalogLoading, setCatalogLoading] = useState(() => !!(entityId && vendorId));

	const refreshCatalog = useCallback(
		(id, partEquipmentVendorId) => {
			setCatalogLoading(true);
			fetchMethod(
				{
					[entityName]: id,
					partEquipmentVendorId,
				},
				'add_in_pr_index'
			)
				.then((res) => {
					const nextCatalog = res && res.length > 0 ? res[0] : null;
					setCatalog(nextCatalog);
				})
				.finally(() => {
					setCatalogLoading(false);
				});
		},
		[entityName, fetchMethod]
	);

	useEffect(() => {
		if (entityId && vendorId) {
			refreshCatalog(entityId, vendorId);
		} else {
			// No vendor to fetch a catalog from — there is no pending request, so
			// settle the loading state and let any cost fallback render.
			setCatalog(null);
			setCatalogLoading(false);
		}
	}, [entityId, refreshCatalog, vendorId]);

	const catalogCurrencyId = useMemo(
		() => nullSafeGetOrElse('partEquipmentVendor.currencyId', catalog, fallbackCurrencyId),
		[catalog, fallbackCurrencyId]
	);

	// Derived synchronously from the loaded catalog, so it is already correct in the
	// same render the default-selection effect runs — no dependence on a round-trip
	// through the parent's disabled prop.
	const currencyMismatch = useMemo(
		() =>
			!!disableOnCurrencyMismatch &&
			!!fallbackCurrencyId &&
			!!catalogCurrencyId &&
			catalogCurrencyId !== fallbackCurrencyId,
		[disableOnCurrencyMismatch, fallbackCurrencyId, catalogCurrencyId]
	);
	const isDisabled = disabled || currencyMismatch;

	useEffect(() => {
		if (!catalogLoading && onCatalogLoad) {
			onCatalogLoad(catalog, catalogCurrencyId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalog, catalogCurrencyId, catalogLoading]);

	const listPrice = nullSafeGet('listPrice', catalog);
	const lastPrice = nullSafeGet('lastPrice', catalog);

	// Fall back to the entity's own cost ONLY when neither list nor last price is
	// available. Gating on the same truthiness the List/Last options use
	// guarantees the Cost option and a List/Last option can never appear together.
	const costFallback = useMemo(() => {
		if (listPrice || lastPrice) return null;
		if (entityCost === null || entityCost === undefined || entityCost === '') return null;
		const numericCost = parseFloat(entityCost);
		return isNaN(numericCost) ? null : numericCost;
	}, [listPrice, lastPrice, entityCost]);

	// When defaultToListPrice is enabled, auto-select a default the moment the
	// catalog has settled — but only if the consumer hasn't already set a value.
	// Priority: List Price, else Last Price, else the entity cost fallback.
	// Intentionally depends on `catalog` only so that clearing or manually
	// changing `value` later does NOT re-fire this effect. The catalogLoading
	// guard ensures cost is only chosen after list/last are genuinely absent.
	useEffect(() => {
		if (!defaultToListPrice) return;
		if (catalogLoading) return;
		// A currency-mismatched catalog's prices are unusable — never auto-select them.
		if (currencyMismatch) return;
		const valueIsEmpty = value === undefined || value === null || value === '';
		if (!valueIsEmpty) return;
		if (listPrice) {
			onChange(parseFloat(listPrice));
		} else if (lastPrice) {
			onChange(parseFloat(lastPrice));
		} else if (costFallback !== null) {
			onChange(costFallback);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalog]);

	const selectedValue = useMemo(() => {
		if (value === undefined || value === null || value === '') return null;
		const numericValue = parseFloat(value);
		if (isNaN(numericValue)) return null;
		if (listPrice && parseFloat(listPrice) === numericValue) return numericValue;
		if (lastPrice && parseFloat(lastPrice) === numericValue) return numericValue;
		if (costFallback !== null && costFallback === numericValue) return numericValue;
		return null;
	}, [costFallback, lastPrice, listPrice, value]);

	const onPriceSelect = useCallback(
		(unitPrice) => {
			onChange(unitPrice);
		},
		[onChange]
	);

	if (catalogLoading) {
		return (
			<div className="ml-2">
				<Spin size="small" />
			</div>
		);
	}

	if (!listPrice && !lastPrice && costFallback === null) {
		return null;
	}

	const radioGroup = (
		<OWRadioGroup
			value={selectedValue}
			onChange={onPriceSelect}
			disabled={isDisabled}
			valueAccessor={(item) => item.value}
			renderRecord={(item) => item.label}
			items={[
				...(listPrice
					? [
							{
								label: `List Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(listPrice)}`,
								value: parseFloat(listPrice),
							},
					  ]
					: []),
				...(lastPrice
					? [
							{
								label: `Last Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(lastPrice)}`,
								value: parseFloat(lastPrice),
							},
					  ]
					: []),
				...(costFallback !== null
					? [
							{
								label: `Cost ${renderCurrencyByCurrencyId(catalogCurrencyId)(costFallback)}`,
								value: costFallback,
							},
					  ]
					: []),
			]}
		/>
	);

	return (
		<div className="ml-2">
			{isDisabled && disabledTooltip ? (
				// Tooltip injects its hover handlers/ref into its child; OWRadioGroup is a
				// custom component that drops them, so wrap it in a real DOM node. The span
				// also captures hover over the disabled (pointer-events: none) radios.
				<Tooltip title={disabledTooltip}>
					<span className="inline-block cursor-not-allowed">{radioGroup}</span>
				</Tooltip>
			) : (
				radioGroup
			)}
		</div>
	);
};

export default PartEquipmentPricePicker;
